import { ShaderSourceParser } from "./Builder/ShaderSourceParser";

export type ShaderAttr = { [key: string]: number };
export type ShaderUniforms = { [key: string]: WebGLUniformLocation | WebGLUniformLocation[] };

type ShaderSettings = {
    shader: WebGLShader,
    uniforms: string[],
    attributes: string[]
}

export interface IShader {
    program: WebGLProgram;
    enableAttributes(): void;
    disableAttributes(): void;
}

export class ShaderBase<TUniforms extends ShaderUniforms, TAttributes extends ShaderAttr> implements IShader {

    public program: WebGLProgram;
    protected get warnAttributeMismatch(): boolean {
        return true;
    }

    public get attributes(): TAttributes {
        return this._attributes as TAttributes;
    }

    public get uniforms(): TUniforms {
        return this._uniforms as TUniforms;
    }

    private _attributes: ShaderAttr;
    private _uniforms: ShaderUniforms;

    constructor(public gl: WebGLRenderingContext, fragmentShaderSource: string, vertexShaderSource: string, settings: string[]) {
        this._attributes = {};
        this._uniforms = {};
        this.program = this.initShaders(fragmentShaderSource, vertexShaderSource, settings);
    }

    public enableAttributes() {
        for (let i in this._attributes) {
            this.gl.enableVertexAttribArray(this._attributes[i]);
        }
    }

    public disableAttributes() {
        for (let i in this._attributes) {
            this.gl.disableVertexAttribArray(this._attributes[i]);
        }
    }

    protected initShaders(fragmentShaderSource: string, vertexShaderSource: string, settings: string[]): WebGLProgram {
        let gl = this.gl;

        let fragmentShader = this.compileShader(fragmentShaderSource, gl.FRAGMENT_SHADER, settings);
        let vertexShader = this.compileShader(vertexShaderSource, gl.VERTEX_SHADER, settings);

        let shaderProgram = gl.createProgram();
        gl.attachShader(shaderProgram, vertexShader.shader);
        gl.attachShader(shaderProgram, fragmentShader.shader);
        gl.linkProgram(shaderProgram);

        if (!gl.getProgramParameter(shaderProgram, gl.LINK_STATUS)) {
            console.error("Could not initialise shaders", gl.getProgramInfoLog(shaderProgram));
        }

        this.postLinkShader(shaderProgram, vertexShader);
        this.postLinkShader(shaderProgram, fragmentShader);

        return shaderProgram;
    }

    private postLinkShader(shaderProgram: WebGLProgram, shader: ShaderSettings) {
        let gl = this.gl;
        for (let attr of shader.attributes) {
            let attrLocation = gl.getAttribLocation(shaderProgram, attr);

            if (attrLocation != -1) {
                this._attributes[attr] = attrLocation;
            } else {
                if (this.warnAttributeMismatch) {
                    console.warn('Shader attribute ' + attr + ' is not found in shader or is unused in shader.');
                }
            }
        }

        for (let uniform of shader.uniforms) {

            let arrayMatch = uniform.match(/(.*?)\[(\d+)\]/);
            if (!arrayMatch) {
                this._uniforms[uniform] = gl.getUniformLocation(shaderProgram, uniform);
            } else {
                let name = arrayMatch[1];
                let size = parseInt(arrayMatch[2]);

                let arr = new Array<WebGLUniformLocation>();
                for (let j = 0; j < size; j++) {
                    arr.push(gl.getUniformLocation(shaderProgram, name + '[' + j + ']'));
                }
                this._uniforms[name] = arr;
            }
        }
    }

    protected compileShader(source: string, shaderType: number, settings: string[]): ShaderSettings {
        let gl = this.gl;
        let shader = gl.createShader(shaderType);

        let parser = new ShaderSourceParser();
        let result = parser.parse(source);

        let header = `#ifdef GL_FRAGMENT_PRECISION_HIGH
precision highp float;
#else
precision mediump float;
#endif
${settings.map(setting => `#define SPI_${setting.toUpperCase()}\n`).join('')}`;

        gl.shaderSource(shader, header + source);
        gl.compileShader(shader);

        if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
            console.log(header + source);
            console.error(gl.getShaderInfoLog(shader));
            console.log(parser.preprocess(source, settings));

            return null;
        }

        return {
            shader: shader,
            uniforms: result.uniforms,
            attributes: result.attributes
        };
    }
}